import { LegacyPackage, LegacyPackageResponse } from 'app/home/common/services/legacy/packages/packages.service';
import { LegacyPackageId, LegacyPackageTitle } from 'app/home/create-quote/forms/compare/compare.types';

interface PackageMetadata {
  id: LegacyPackageId; // Make sure id matches the expected type
  presentedTitle: string;
  pillTitle: string;
  buttonTitle: string;
  bullets: string[];
}

type PackageMetadataMap = {
  [key in LegacyPackageTitle]: PackageMetadata;
};

export const packageTitleMetadata: PackageMetadataMap = {
  'Kin Basics': {
    id: 'kin-basics',
    presentedTitle: 'Basics',
    pillTitle: 'The Basics',
    buttonTitle: 'Select Kin Basics',
    bullets: ['Most basic coverage', 'Lowest premiums', 'Higher out-of-pocket costs for claims'],
  },
  'Kin Plus': {
    id: 'kin-plus',
    presentedTitle: 'Plus',
    pillTitle: 'Our Best Value',
    buttonTitle: 'Select Kin Plus',
    bullets: ['Good coverage', 'Balances premiums and deductibles', 'Lower out-of-pocket costs for claims'],
  },
  'Kin Premium': {
    id: 'kin-premium',
    presentedTitle: 'Premium',
    pillTitle: 'Enhanced Protection',
    buttonTitle: 'Select Kin Premium',
    bullets: ['More robust coverage', 'Lower deductibles', 'Lowest out-of-pocket costs for claims'],
  },
};

export function mapComparePackageData(packages: LegacyPackageResponse, isWindMitApplicable: boolean) {
  const comparePackageData = packages
    .filter((pkg) => Object.keys(packageTitleMetadata).includes(pkg.title as string))
    .map((pkg) => {
      const legacyTitle = pkg.title as LegacyPackageTitle;

      const { id } = packageTitleMetadata[legacyTitle];
      const { presentedTitle } = packageTitleMetadata[legacyTitle];
      const { buttonTitle } = packageTitleMetadata[legacyTitle];
      const { bullets } = packageTitleMetadata[legacyTitle];

      // Get both wind mit ON and OFF totals for each package
      const totalWithWindMit = getFullPayTotal(pkg, true);
      const totalWithoutWindMit = getFullPayTotal(pkg, false);

      // Use the appropriate total based on current state
      const total = isWindMitApplicable ? totalWithWindMit : totalWithoutWindMit;

      const coverageAttributes = pkg.coverage_attributes;
      const { pillTitle } = packageTitleMetadata[legacyTitle];

      return {
        id,
        legacyTitle,
        presentedTitle,
        pillTitle,
        total,
        totalWithWindMit,
        totalWithoutWindMit,
        coverageAttributes,
        buttonTitle,
        bullets,
      };
    });
  return comparePackageData;
}

// Returns the total amount for the first payment option with wind_mit_applied set to windMitApplied. If no payment option is found, returns 0.
function getFullPayTotal(legacyPackage: LegacyPackage, windMitApplied: boolean) {
  // Look for a matching payment option with the specific wind mit status
  const matchingOption = legacyPackage.payment_options.find((option) => option.wind_mit_applied === windMitApplied && option.payment_type === 'full');

  // If a specific match is found, use it; otherwise fall back to first option
  const selectedOption = matchingOption || legacyPackage.payment_options[0];
  return parseFloat(selectedOption.total_amount) || 0;
}

// Returns true if any of the packages have a payment option with wind_mit_applied set to true
export function isCheckWindMitApplicable(packages: LegacyPackageResponse) {
  const fullPayPaymentOptions = packages.flatMap((pkg) => pkg.payment_options);
  return fullPayPaymentOptions.some((option) => option.wind_mit_applied);
}
